import { ACCENT_DOT, type Accent } from '@/lib/accent'
import {
  type PreviousRun,
  type TaskRuntime,
  formatDuration,
  formatInputSize,
  formatThroughput,
} from '@/lib/taskRuntime'
import { TASK_STATUS_TONE } from '@/lib/taskStatusDisplay'
import { cn } from '@/lib/utils'

import { EmptyState } from '@droidmap/frontend-ui'
import type { Task } from '@droidmap/project-service-contract'

import { SectionLabel } from './SectionLabel'

export interface RunComparisonProps {
  /** The run being looked at. */
  task: Task
  runtime: TaskRuntime
  /** Finished prior runs of the same project, newest first. */
  previous: PreviousRun[]
  /** Median runtime of the size-comparable prior runs, when there are any. */
  medianComparableMs?: number
  className?: string
}

/**
 * Bar length as a share of the scale, floored so it's visible and clamped so
 * an off-scale run can't run past the track.
 */
const barPercent = (durationMs: number, scaleMs: number): number =>
  scaleMs > 0 ? Math.min(100, Math.max(2, (durationMs / scaleMs) * 100)) : 0

/** `2.4×` / `0.6×` — how this run's input compares in size with the subject's. */
const sizeRatioLabel = (
  reference: TaskRuntime,
  candidate: TaskRuntime,
): string | undefined => {
  if (!reference.gigapixels || !candidate.gigapixels) return undefined
  const ratio = candidate.gigapixels / reference.gigapixels
  return `${ratio < 1 ? ratio.toFixed(2) : ratio.toFixed(1)}× size`
}

function RunRow({
  label,
  tone,
  runtime,
  scaleMs,
  emphasis,
  muted,
  note,
}: {
  label: string
  tone: Accent
  runtime: TaskRuntime
  scaleMs: number
  /** The subject of the comparison — brighter text and an accent bar. */
  emphasis?: boolean
  /**
   * Outside the comparable set: faded, because its bar is not on the same
   * scale as the rest (it may be clamped) and its length is not a comparison
   * the panel is making.
   */
  muted?: boolean
  /** Why this run is (or isn't) a like-for-like comparison. */
  note?: string
}) {
  const size = formatInputSize(runtime)
  const throughput = formatThroughput(runtime)
  return (
    <li className="flex flex-col gap-1.5 py-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <span
            aria-hidden
            className={cn(
              'h-2 w-2 flex-shrink-0 rounded-full',
              ACCENT_DOT[tone],
              runtime.live && 'animate-pulse',
            )}
          />
          <span
            className={cn(
              'truncate text-[13px]',
              emphasis ? 'text-text font-medium' : 'text-text-dim',
            )}
          >
            {label}
          </span>
        </span>
        <span
          className={cn(
            'flex-shrink-0 font-mono text-[12px] tabular-nums',
            runtime.live
              ? 'text-warning'
              : emphasis
                ? 'text-text'
                : 'text-text-dim',
          )}
        >
          {runtime.durationMs === undefined
            ? '—'
            : `${formatDuration(runtime.durationMs)}${
                runtime.live ? ' elapsed' : ''
              }`}
        </span>
      </div>

      {/* Duration bar. A live run's bar grows with it, which is the point —
          you can watch it cross the previous runs. Muted bars belong to runs
          the panel is explicitly *not* comparing, so their length must not
          invite a reading. */}
      <div className="bg-bg-2 h-1.5 w-full overflow-hidden rounded-full">
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-500',
            runtime.live
              ? 'bg-warning'
              : emphasis
                ? 'bg-accent'
                : 'bg-accent-2',
            muted && 'bg-border-strong opacity-50',
          )}
          style={{
            width: `${barPercent(runtime.durationMs ?? 0, scaleMs)}%`,
          }}
        />
      </div>

      <div className="text-text-mute flex flex-wrap items-baseline gap-x-2 font-mono text-[10px] tracking-wide">
        <span>{size ?? 'size not recorded'}</span>
        {throughput && <span>· {throughput}</span>}
        {note && <span className="text-text-dim">· {note}</span>}
      </div>
    </li>
  )
}

/**
 * This run set against the project's previous runs.
 *
 * The design decision worth stating: bars are scaled by **duration**, but the
 * comparison is only claimed between runs of **similar input size**. Runtime
 * is not a smooth function of settings — droidmap#2476 measured 1635 s vs
 * 10927 s on identical input and instance type, purely from
 * `matcherNeighbors` — so ranking every run in a project by duration would
 * mostly rank them by how much imagery went in, and any residual difference
 * would look like a speed regression rather than a different reconstruction.
 *
 * So each prior run carries its input size and its per-gigapixel rate, runs
 * outside ±25% of this run's size are drawn muted and labelled with their
 * size ratio, and the headline baseline is the **median of the comparable
 * runs only**. Where nothing is comparable the panel says so instead of
 * quietly widening the window.
 */
export function RunComparison({
  task,
  runtime,
  previous,
  medianComparableMs,
  className,
}: RunComparisonProps) {
  const comparableCount = previous.filter((run) => run.comparable).length

  // Scale the bars to this run plus the runs it is actually being compared
  // with — not to the longest thing on screen. A 6h mosaic in the same project
  // would otherwise squash 27m and 30m into indistinguishable stubs and throw
  // away exactly the resolution the panel exists to provide. Runs outside the
  // comparable set clamp at 100% and are drawn faded (see {@link RunRow}).
  const comparableMs = previous
    .filter((run) => run.comparable)
    .map((run) => run.runtime.durationMs ?? 0)
  const scaleMs =
    Math.max(runtime.durationMs ?? 0, ...comparableMs) ||
    // Nothing comparable (and nothing running): fall back to the full set so
    // the bars still carry relative magnitude.
    Math.max(0, ...previous.map((run) => run.runtime.durationMs ?? 0))

  const delta =
    medianComparableMs !== undefined && runtime.durationMs !== undefined
      ? runtime.durationMs - medianComparableMs
      : undefined

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div>
        <SectionLabel>This run</SectionLabel>
        <ul className="mt-1">
          <RunRow
            label={task.name}
            tone={TASK_STATUS_TONE[task.status]}
            runtime={runtime}
            scaleMs={scaleMs}
            emphasis
          />
        </ul>
      </div>

      <div>
        <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
          <SectionLabel>Previous runs in this project</SectionLabel>
          {medianComparableMs !== undefined && (
            <span className="text-text-dim font-mono text-[11px]">
              median of {comparableCount} comparable ·{' '}
              <span className="text-text">
                {formatDuration(medianComparableMs)}
              </span>
              {delta !== undefined && (
                // Deliberately uncoloured. Green-for-faster would assert that
                // a shorter run is a better one, and #2476 is the
                // counterexample: the fast jobs there produced 22% of the
                // output — an incomplete reconstruction, not a cheaper one.
                // The delta is a fact; whether it is good is the reader's call.
                <span className="ml-1.5">
                  ({delta > 0 ? '+' : '−'}
                  {formatDuration(Math.abs(delta))}
                  {runtime.live ? ' so far' : ''})
                </span>
              )}
            </span>
          )}
        </div>

        {previous.length === 0 ? (
          <EmptyState
            title="No previous runs"
            body="Nothing else in this project has finished a run yet, so there is no baseline to compare against."
          />
        ) : (
          <>
            <ul className="divide-border divide-y">
              {previous.map(
                ({ task: previousTask, runtime: prev, comparable }) => (
                  <RunRow
                    key={previousTask.id}
                    label={previousTask.name}
                    tone={TASK_STATUS_TONE[previousTask.status]}
                    runtime={prev}
                    scaleMs={scaleMs}
                    {...(!comparable && {
                      muted: true,
                      note:
                        sizeRatioLabel(runtime, prev) ?? 'not size-comparable',
                    })}
                  />
                ),
              )}
            </ul>
            {comparableCount === 0 && (
              <p className="text-text-mute mt-2 text-[11px]">
                None of these ran on a similar amount of imagery, so their
                durations are not a baseline for this one.
              </p>
            )}
          </>
        )}
      </div>

      <p className="text-text-mute border-border border-t pt-3 text-[11px] leading-relaxed">
        Runtime is governed by the dataset and the reconstruction settings, not
        by a smooth multiplier: two jobs on identical input and instance type
        have been measured at 27m and 3h 2m purely from a different{' '}
        <span className="font-mono">matcherNeighbors</span> value. Read a
        difference between runs of similar size as a different reconstruction
        before reading it as a slowdown.
      </p>
    </div>
  )
}

export default RunComparison
